import React, { useState } from 'react';
import { CognitoUser, CognitoUserAttribute, AuthenticationDetails } from 'amazon-cognito-identity-js';
import userPool from '../../components/userPool.js';
import greetingsData from '../../components/greetings.json';
import { Player } from '@lottiefiles/react-lottie-player';
import backgroundAnimation from '../../assets/animations/spinner.json'; // adjust path as needed
import { ReactComponent as EyeIcon } from '../../assets/icons/svg/eye.svg';
import { ReactComponent as EyeOffIcon } from '../../assets/icons/svg/eye-off.svg';
import styles from './Login.module.css';
import { Button } from '../../theme/button.js';
import { useNavigate } from 'react-router-dom';
import LoaderWithDots from '../../components/loaderWithDots.js';
import secureLocalStorage from "react-secure-storage";
import Banner from '../../components/Banner/Banner.js';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from "react-router-dom";

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const location = useLocation();

  // form type: 'login' | 'register' | 'forgot'
  const [formType, setFormType] = useState('login');

  // login fields and state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmReset, setConfirmReset] = useState('');
  const [verificationCode, setVerificationCode] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleShowPassword = () => setShowPassword(prev => !prev);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(prev => !prev);
  const toggleShowNewPassword = () => setShowNewPassword(prev => !prev);
  const toggleShowNewConfirmPassword = () => setShowConfirmNewPassword(prev => !prev);

  const handleSignIn = () => {
    setLoading(true);
    setError('');

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();

        // Update AuthContext
        login(cognitoUser, idToken);

        // Fetch user attributes for greeting
        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            console.error('Failed to get user attributes:', err);
            setLoading(false);
            return;
          }

          const attrs = {};
          attributes.forEach(attr => (attrs[attr.getName()] = attr.getValue()));

          const greeting = getRandomGreeting();

          localStorage.setItem(
            'user',
            JSON.stringify({
              email,
              greeting,
              firstName: attrs.given_name || '',
              lastName: attrs.family_name || ''
            })
          );

          setLoading(false);

          const params = new URLSearchParams(location.search);
          const redirect = params.get("redirect");
          if(redirect === 'subscribe'){
            navigate('/dashboard', { state: { subscribe: true } });
          } 
          else{
            navigate('/dashboard');
          }
          
        });
      },

      onFailure: (err) => {
        setError(err.message || 'Authentication failed');
        setLoading(false);
      },
    });
  };

  const handleSignUp = (e) => {
      // Clear previous error
      setError('');

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Invalid Email: Please enter a valid email address');
        return;
      }

      // Password validation
      const errors = [];

      if (password.length < 8) {
        errors.push('Password must be at least 8 characters long.');
      }

      const commonWords = ['password', '123456', 'qwerty', 'admin'];
      if (commonWords.some(word => password.toLowerCase().includes(word))) {
        errors.push('Avoid common words or easily guessable patterns.');
      }

      if (password.toLowerCase().includes(email.split('@')[0].toLowerCase())) {
        errors.push('Avoid using your username or email in the password.');
      }

      if (!/[A-Z]/.test(password)) {
        errors.push('Password must contain at least one capital letter.');
      }

      if (!/\d/.test(password)) {
        errors.push('Password must contain at least one digit.');
      }

      if (!/[@$!%*?&]/.test(password)) {
        errors.push('Password must contain at least one special character.');
      }

      if (errors.length > 0) {
        setError(errors.join('\n'));
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match. Please make sure the passwords match.');
        return;
      }


      // Proceed with AWS Cognito signup below...
      setError('');

      const attributeList = [
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({ Name: 'email', Value: email }),
      ];

      userPool.signUp(email, password, attributeList, null, async (err, result) => {
        if (err) {
          setError(err.message || JSON.stringify(err));
          return;
        }

        try {
          // 👇 Call your user creation Lambda after Cognito signup
          await queryUsersTableUserCreation(email, result.userSub);

          alert(`Sign up successful for ${result.user.getUsername()}. Please check your email to confirm your account.`);

          // reset form
          setFormType('login');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setFirstName('');
          setLastName('');
        } catch (postError) {
          console.error("User creation API failed:", postError);
          alert("Account created in Cognito, but user record creation failed.");
        }
      });
  };

  const queryUsersTableUserCreation = async (userEmail, cognitoUsername) => {

    const API_URL = 'https://pbkhrrgjtf.execute-api.us-east-1.amazonaws.com/prod/userCreation';
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({  
          email: userEmail,
          username: cognitoUsername,
        }),
      });
      const data = await response.json();
      return (data);

    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleForgotPassword = () => {
    setError('');

    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    user.forgotPassword({
      onSuccess: (data) => {
      },

      onFailure: (err) => {
        setError(err.message || 'Failed to send reset code');
      },

      // Optional: If you want to handle the delivery details:
      inputVerificationCode: () => {
        // This callback fires when Cognito is ready for user to enter the code
        // You might want to change the form to show "Enter reset code and new password"
        alert('Please check your email for the verification code.');
        setConfirmReset(true);
      },
    });
  };

  const handleConfirmReset = () => {
    // Clear previous error
    setError('');

    // Password validation
    const errors = [];

    if (newPassword.length < 8) {
      errors.push('Password must be at least 8 characters long.');
    }

    const commonWords = ['password', '123456', 'qwerty', 'admin'];
    if (commonWords.some(word => newPassword.toLowerCase().includes(word))) {
      errors.push('Avoid common words or easily guessable patterns.');
    }

    if (email && newPassword.toLowerCase().includes(email.split('@')[0].toLowerCase())) {
      errors.push('Avoid using your username or email in the password.');
    }

    if (!/[A-Z]/.test(newPassword)) {
      errors.push('Password must contain at least one capital letter.');
    }

    if (!/\d/.test(newPassword)) {
      errors.push('Password must contain at least one digit.');
    }

    if (!/[@$!%*?&]/.test(newPassword)) {
      errors.push('Password must contain at least one special character.');
    }

    if (newPassword !== confirmPassword) {
      errors.push('Passwords do not match. Please make sure the passwords match.');
    }

    if (errors.length > 0) {
      setError(errors.join('\n')); // You could also join with <br /> or map to <li> if rendering HTML
      return;
    }

    // Call Cognito to confirm password reset
    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    user.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        alert('Password reset successful! You can now log in with your new password.');
        setConfirmReset(false);
        setFormType('login'); // Send back to login or reset flow
      },
      onFailure: (err) => {
        setError(err.message || 'Failed to reset password');
      },
    });
  };


  const handleSubmit = (event) => {
    event.preventDefault(); // prevent page reload
    if (formType === 'login') {
      handleSignIn();
    }

    if (formType === 'register') {
      handleSignUp();
    }

    if (formType === 'forgot') {
      handleForgotPassword();
    }

  };

  const getRandomGreeting = () => {
    const greetings = greetingsData.greetings;
    return greetings[Math.floor(Math.random() * greetings.length)];
  };

  const handleGoogleSignIn = () => {
    const cognitoDomain = "https://loggable.auth.us-east-1.amazoncognito.com"; 
    const clientId = "5vbhldlu8uhcvba8rvvngogjpv";
    const redirectUri =
    window.location.hostname === "localhost"
      ? "http://localhost:3000/login/callback"
      : "https://app.loggable.net/login/callback";
    const scope = "openid profile email";

    const url =
      `${cognitoDomain}/oauth2/authorize` +
      `?identity_provider=Google` +
      `&response_type=code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}`;

    window.location.href = url;
  };


  // === Renders for each form ===

  const renderLoginForm = () => (
    <form onSubmit={handleSubmit}>
      {error && <p className={styles.errorMessage}>{error}</p>}
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>Email</label>
        <input
          type="email"
          placeholder="email@loggable.net"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={styles.inputField}
          autoComplete="email"
          required
        />
      </div>

      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>Password</label>
        <div className={styles.passwordRow}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="********"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={styles.inputField}
            autoComplete="current-password"
            required
          />
          <button type="button" onClick={toggleShowPassword} className={styles.eyeButton}>
            {showPassword ? <EyeOffIcon className={styles.eyeImage} /> : <EyeIcon className={styles.eyeImage} />}
          </button>
        </div>
      </div>

      <div className={styles.loginButtonContainer}>
        <Button className={styles.loginButton} type="submit" disabled={loading}>
          {loading ? 'Signing In...' : 'Login'}
        </Button>
        <Button
          type="button"
          className={styles.googleButton}
          onClick={handleGoogleSignIn}
        >
          <img src="/assets/icons/google.png" alt="Google" />
          Sign in with Google
        </Button>
        <p className={styles.forgotText}>
          <button
            type="button"
            onClick={() => { setFormType('forgot'); setError(''); }}
            className={styles.linkButton}
          >
            Forgot Password?
          </button>
        </p>
      </div>
    </form>
  );

  const renderRegisterForm = () => (
    <>
      <form onSubmit={handleSubmit}>
        <h2 className={`${styles.headerText} ${styles.center}`}>Sign Up for Loggable</h2>
        {error && <p className={styles.errorMessage}>{error}</p>}
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>First Name</label>
          <input
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={styles.inputField}
            required
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>Last Name</label>
          <input
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={styles.inputField}
            required
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>Email</label>
          <input
            type="email"
            placeholder="email@loggable.net"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={styles.inputField}
            autoComplete="email"
            required
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>Password</label>
          <div className={styles.passwordRow}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="********"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.inputField}
              autoComplete="current-password"
              required
            />
            <button type="button" onClick={toggleShowPassword} className={styles.eyeButton}>
              {showPassword ? <EyeOffIcon className={styles.eyeImage} /> : <EyeIcon className={styles.eyeImage} />}
            </button>
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>Confirm Password</label>
          <div className={styles.passwordRow}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="********"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={styles.inputField}
              autoComplete="current-password"
              required
            />
            <button type="button" onClick={toggleShowConfirmPassword} className={styles.eyeButton}>
              {showConfirmPassword ? <EyeOffIcon className={styles.eyeImage} /> : <EyeIcon className={styles.eyeImage} />}
            </button>
          </div>
        </div>
        <div className={styles.centerContainer}>
          <Button className={styles.loginButton} type="submit" disabled={loading}>
              Sign Up
          </Button>
        </div>
      </form>
      <p className={styles.passwordRequirements}>
        <em>*** Password Requirements ***</em><br /><br />
        At least 8 characters long.<br />
        Avoid common words or easily guessable patterns.<br />
        Avoid using your username or email in the password.<br />
        At least one capital letter.<br />
        At least one digit.<br />
        At least one special character (e.g., @$!%*?&)<br /><br />
      </p>
      <p className={`${styles.backLogin} ${styles.center}`}>
        <button
          type="button"
          onClick={() => { setFormType('login'); setError(''); }}
          className={styles.linkButton}
        >
          Back to Login
        </button>
      </p>
    </>
  );
  

  const renderForgotForm = () => {
    if (!confirmReset) {
      // Step 1: Email entry
      return (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleForgotPassword();
            }}
          >
            <h2 className={`${styles.headerText} ${styles.center}`}>Forgot your password?</h2>
            <h2 className={`${styles.headerInfo} ${styles.center}`}>
              Please enter your email address to receive a password reset code
            </h2>
            {error && <p className={styles.errorMessage}>{error}</p>}
            <div className={styles.inputGroup}>
              <input
                type="email"
                placeholder="email@loggable.net"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={styles.inputField}
                autoComplete="email"
                required
              />
            </div>
            <div className={styles.centerContainer}>
              <Button className={styles.loginButton} type="submit" disabled={loading}>
                Request Reset Code
              </Button>
            </div>
          </form>
          <p className={`${styles.backLogin} ${styles.center}`}>
            <button
              type="button"
              onClick={() => {
                setFormType('login');  // Go back to login screen
                setError('');
              }}
              className={styles.linkButton}
            >
              Back to Login
            </button>
          </p>
        </>
      );
    } else {
      // Step 2: Confirm reset with code & new password
      return (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmReset();
            }}
          >
            <h2 className={`${styles.headerText} ${styles.center}`}>Reset your password</h2>
            <h2 className={`${styles.headerInfo} ${styles.center}`}>
              Enter the code sent to your email and your new password
            </h2>
            {error && <p className={styles.errorMessage}>{error}</p>}
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Verification Code</label>
              <input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className={styles.inputField}
                required
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>New Password</label>
              <div className={styles.passwordRow}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="********"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={styles.inputField}
                  autoComplete="new-password"
                  required
                />
                <button type="button" onClick={toggleShowNewPassword} className={styles.eyeButton}>
                  {showNewPassword ? <EyeOffIcon className={styles.eyeImage} /> : <EyeIcon className={styles.eyeImage} />}
                </button>
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Confirm New Password</label>
              <div className={styles.passwordRow}>
                <input
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  placeholder="********"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={styles.inputField}
                  autoComplete="new-password"
                  required
                />
                <button type="button" onClick={toggleShowNewConfirmPassword} className={styles.eyeButton}>
                  {showConfirmNewPassword ? <EyeOffIcon className={styles.eyeImage} /> : <EyeIcon className={styles.eyeImage} />}
                </button>
              </div>
            </div>
            <div className={styles.centerContainer}>
              <Button className={styles.loginButton} type="submit" disabled={loading}>
                Confirm Password Reset
              </Button>
            </div>
          </form>
          <p className={`${styles.backLogin} ${styles.center}`}>
            <button
              type="button"
              onClick={() => {
                setConfirmReset(false);  // Back to step 1 email input
                setError('');
              }}
              className={styles.linkButton}
            >
              Back to Email Entry
            </button>
          </p>
        </>
      );
    }
  };


  return (
    <div className={styles.loginwrapper}>

      <a href="https://loggable.net" rel="noopener noreferrer">
        <img
          src="/assets/loggableLogos/LoggableIconHorizontal.png"
          alt="Loggable Logo"
          className={styles.navLogo}
        />
      </a>

      <div className={styles.logincard}>
        <div className={styles.formTransition} key={formType}>
          {formType === 'login' && renderLoginForm()}
          {formType === 'register' && renderRegisterForm()}
          {formType === 'forgot' && renderForgotForm()}
        </div>
      </div>


      {formType === 'login' && (
        <p className="signup-text">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => { setFormType('register'); setError(''); }}
            className={styles.linkButton}
          >
            Sign up
          </button>
        </p>
      )}

      {loading && (
        <div className={styles.blurContainer}>
          <div className={styles.loaderContainer}>
            <Player
              autoplay
              loop
              src={backgroundAnimation}
              className={styles.spinner}
            />
            <LoaderWithDots isLoading={loading} text="Signing In" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
